import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import Layout from './components/Layout';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import HotelManagerDashboard from './pages/HotelManagerDashboard';
import TechnicianDashboard from './pages/TechnicianDashboard';
import HousekeepingDashboard from './pages/HousekeepingDashboard';
import FrontofficeDashboard from './pages/FrontofficeDashboard';
import TicketsPage from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import HotelsPage from './pages/HotelsPage';
import UsersPage from './pages/UsersPage';
import HeatmapPage from './pages/HeatmapPage';
import ConsumptionPage from './pages/ConsumptionPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ScheduledMaintenancePage from './pages/ScheduledMaintenancePage';
import ScheduledMaintenanceDetailPage from './pages/ScheduledMaintenanceDetailPage';
import UserManualPage from './pages/UserManualPage';
import TechnicianProductivityPage from './pages/TechnicianProductivityPage';
import ContractMaintenancePage from './pages/ContractMaintenancePage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import InvestorLandingPage from './pages/InvestorLandingPage';
import ProfilePage from './pages/ProfilePage';
import BoardPage from './pages/BoardPage';
import PatchNotesPage from './pages/PatchNotesPage';
import SystemHealthPage from './pages/SystemHealthPage';
import OnlineUsersPage from './pages/OnlineUsersPage';
import ElevatorsPage from './pages/ElevatorsPage';
import AdminToolsPage from './pages/AdminToolsPage';
import NotificationLogsPage from './pages/NotificationLogsPage';
import TicketQualityPage from './pages/TicketQualityPage';
import HotelRequestsInboxPage from './pages/HotelRequestsInboxPage';
import HotelRequestDetailPage from './pages/HotelRequestDetailPage';
import EmergencyContactsPage from './pages/EmergencyContactsPage';
import ElevatorStatsPage from './pages/ElevatorStatsPage';
import IntelCenterPage from './pages/IntelCenterPage';
import AssetGenealogyPage from './pages/AssetGenealogyPage';
import DisciplinaPage from './pages/DisciplinaPage';
import TechPerformancePage from './pages/TechPerformancePage';
import GeolocationPage from './pages/GeolocationPage';
import MockupFrontOffice from './pages/MockupFrontOffice';
import MockupSuperAdmin from './pages/MockupSuperAdmin';
import MockupHub from './pages/mockup/MockupHub';
import MockupV2Index from './pages/mockup/v2/IndexV2';
import MockupV2A from './pages/mockup/v2/ProposalA';
import MockupV2B from './pages/mockup/v2/ProposalB';
import MockupV2C from './pages/mockup/v2/ProposalC';
import MockupV2CTickets from './pages/mockup/v2/ProposalCTickets';
import MockupV2CScheduled from './pages/mockup/v2/ProposalCScheduled';
import MockupV2CConsumption from './pages/mockup/v2/ProposalCConsumption';
import MockupV2Mobile from './pages/mockup/v2/MobileIndex';
import MockupV2CardsPreview from './pages/mockup/v2/CardsPreview';
import SensitiveSectionGuard from './components/SensitiveSectionGuard';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import MobileBottomNav from './components/MobileBottomNav';

import './App.css';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user.role) {
      case 'super_admin':
        return <Navigate to="/dashboard" replace />;
      case 'hotel_manager':
        return <Navigate to="/hotel" replace />;
      case 'technician':
        return <Navigate to="/tecnico" replace />;
      case 'housekeeping':
        return <Navigate to="/housekeeping" replace />;
      case 'frontoffice':
        return <Navigate to="/frontoffice" replace />;
      case 'sede':
        return <Navigate to="/richieste" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

// Redirect based on role
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.role) {
    case 'super_admin':
      return <Navigate to="/dashboard" replace />;
    case 'hotel_manager':
      return <Navigate to="/hotel" replace />;
    case 'capo_manutentore':
      return <Navigate to="/tickets" replace />;
    case 'technician':
      return <Navigate to="/tecnico" replace />;
    case 'housekeeping':
      return <Navigate to="/housekeeping" replace />;
    case 'frontoffice':
      return <Navigate to="/frontoffice" replace />;
    case 'sede':
      return <Navigate to="/richieste" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function AppRoutes() {
  const { user } = useAuth();
  const { applyUserTheme } = useTheme();

  // Quando l'utente cambia (login / logout / refresh), applica il suo ui_theme
  React.useEffect(() => {
    if (user && typeof user === 'object') {
      applyUserTheme(user.ui_theme || 'default');
    }
  }, [user, applyUserTheme]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/presentazione" element={<InvestorLandingPage />} />
      <Route path="/login" element={user ? <RoleBasedRedirect /> : <LoginPage />} />

      {/* MOCKUP routes — anteprima visiva pubblica, non collegate al codice live */}
      <Route path="/mockup" element={<MockupHub />} />
      <Route path="/mockup/fo" element={<MockupFrontOffice />} />
      <Route path="/mockup/admin" element={<MockupSuperAdmin />} />

      {/* MOCKUP V2 — 3 direzioni di redesign (Operational, Boutique, Data Intel) */}
      <Route path="/mockup/v2" element={<MockupV2Index />} />
      <Route path="/mockup/v2/a" element={<MockupV2A />} />
      <Route path="/mockup/v2/b" element={<MockupV2B />} />
      <Route path="/mockup/v2/c" element={<MockupV2C />} />
      <Route path="/mockup/v2/c/tickets" element={<MockupV2CTickets />} />
      <Route path="/mockup/v2/c/scheduled" element={<MockupV2CScheduled />} />
      <Route path="/mockup/v2/c/consumption" element={<MockupV2CConsumption />} />
      <Route path="/mockup/v2/mobile" element={<MockupV2Mobile />} />
      <Route path="/mockup/v2/cards-preview" element={<MockupV2CardsPreview />} />

      {/* Protected routes with layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Default redirect */}
        <Route index element={<RoleBasedRedirect />} />

        {/* Super Admin routes */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Utenti">
                <UsersPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager']}>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="digital-twin"
          element={<Navigate to="/dashboard" replace />}
        />
        <Route
          path="digital-twin/:hotelId"
          element={<Navigate to="/dashboard" replace />}
        />
        <Route
          path="command-wall"
          element={<Navigate to="/dashboard" replace />}
        />

        {/* Hotel Manager routes */}
        <Route
          path="hotel"
          element={
            <ProtectedRoute allowedRoles={['hotel_manager']}>
              <HotelManagerDashboard />
            </ProtectedRoute>
          }
        />

        {/* Technician routes */}
        <Route
          path="tecnico"
          element={
            <ProtectedRoute allowedRoles={['technician']}>
              <TechnicianDashboard />
            </ProtectedRoute>
          }
        />

        {/* Housekeeping routes */}
        <Route
          path="housekeeping"
          element={
            <ProtectedRoute allowedRoles={['housekeeping']}>
              <HousekeepingDashboard />
            </ProtectedRoute>
          }
        />

        {/* Frontoffice routes */}
        <Route
          path="frontoffice"
          element={
            <ProtectedRoute allowedRoles={['frontoffice']}>
              <FrontofficeDashboard />
            </ProtectedRoute>
          }
        />

        {/* Shared routes */}
        <Route
          path="tickets"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore', 'technician', 'frontoffice']}>
              <TicketsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ascensori"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'frontoffice', 'capo_manutentore', 'sede']}>
              <ElevatorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ascensori/statistiche"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'sede']}>
              <ElevatorStatsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tickets/:id"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore', 'technician', 'frontoffice', 'housekeeping']}>
              <TicketDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="hotels"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Hotel">
                <HotelsPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="heatmap"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore']}>
              <HeatmapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="consumi"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'sede']}>
              <ConsumptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="scheduled"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore']}>
              <ScheduledMaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="scheduled/:id"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore', 'technician']}>
              <ScheduledMaintenanceDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="produttivita"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager']}>
              <TechnicianProductivityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="contratti-manutenzione"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'sede']}>
              <ContractMaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="impostazioni-notifiche"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Notifiche Email">
                <NotificationSettingsPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="manuale"
          element={
            <ProtectedRoute>
              <UserManualPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="profilo"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="bacheca"
          element={
            <ProtectedRoute>
              <BoardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="novita"
          element={
            <ProtectedRoute>
              <PatchNotesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="system-health"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Stato Sistema">
                <SystemHealthPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="strumenti-dati"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Strumenti Dati">
                <AdminToolsPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="log-notifiche"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Log Notifiche">
                <NotificationLogsPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="qualita-ticket"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore', 'frontoffice']}>
              <TicketQualityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="richieste"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore', 'sede']}>
              <HotelRequestsInboxPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="richieste/:id"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore', 'sede']}>
              <HotelRequestDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="emergenze"
          element={
            <ProtectedRoute>
              <EmergencyContactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="utenti-online"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Utenti Online">
                <OnlineUsersPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="intel"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager']}>
              <IntelCenterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="genealogia/:hotelId/:area"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore']}>
              <AssetGenealogyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="disciplina"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager']}>
              <DisciplinaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="performance/:userId"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'hotel_manager', 'capo_manutentore']}>
              <TechPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="mia-performance"
          element={
            <ProtectedRoute allowedRoles={['technician']}>
              <TechPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="geolocalizzazione"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SensitiveSectionGuard sectionName="Geolocalizzazione">
                <GeolocationPage />
              </SensitiveSectionGuard>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <MobileBottomNav />
          <PWAInstallPrompt />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
